export class BackgroundExportManager {
  private static instance: BackgroundExportManager;
  private isExporting = false;
  private shouldContinue = true;
  private progressCallbacks: Set<(progress: number, status: string) => void> = new Set();
  private currentProgress = 0;
  private currentStatus = '';
  private heartbeatInterval: number | null = null;
  private visibilityHandler: (() => void) | null = null;
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => string | undefined) | null = null;

  private features = {
    hasAudioAPI: false,
    hasNotifications: false,
    hasRAF: false,
    hasPromises: false,
    hasLocalStorage: false,
    hasPerformanceMemory: false,
  };

  private audioContext: AudioContext | null = null;
  private rafId: number | null = null;
  private yieldCount = 0;

  /**
   * Safe localStorage wrapper that handles all edge cases
   */
  private storage = {
    setItem: (key: string, value: string): void => {
      if (!this.features.hasLocalStorage) return;

      try {
        localStorage.setItem(key, value);
      } catch (e) {
        if (e.code === 22 || e.code === 1014) {
          try {
            this.cleanupLocalStorage();
            localStorage.setItem(key, value);
          } catch (retryError) {
            console.warn('Storage unavailable:', retryError);
          }
        }
      }
    },

    getItem: (key: string): string | null => {
      if (!this.features.hasLocalStorage) return null;

      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },

    removeItem: (key: string): void => {
      if (!this.features.hasLocalStorage) return;

      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore
      }
    },
  };

  private constructor() {
    this.detectFeatures();
    this.setupEventHandlers();
    this.cleanupStaleExports();
    this.initOptionalEnhancements();
  }

  public static getInstance(): BackgroundExportManager {
    if (!BackgroundExportManager.instance) {
      BackgroundExportManager.instance = new BackgroundExportManager();
    }
    return BackgroundExportManager.instance;
  }

  /**
   * Detect available browser features for progressive enhancement
   */
  private detectFeatures(): void {
    this.features.hasAudioAPI = !!((window as any).AudioContext || (window as any).webkitAudioContext);

    this.features.hasNotifications = 'Notification' in window;

    this.features.hasRAF = typeof requestAnimationFrame !== 'undefined';

    this.features.hasPromises = typeof Promise !== 'undefined';

    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      this.features.hasLocalStorage = true;
    } catch (e) {
      this.features.hasLocalStorage = false;
    }

    this.features.hasPerformanceMemory = !!(performance as any).memory;

    // console.warn('Export Manager Features:', this.features);
  }

  /**
   * Initialize optional performance enhancements
   */
  private initOptionalEnhancements(): void {
    if (this.features.hasAudioAPI) {
      this.initAudioContext();
    }
  }

  /**
   * Audio Context for preventing browser throttling
   */
  private initAudioContext(): void {
    try {
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;

      this.audioContext = new AudioContextClass();

      if (this.audioContext.state === 'suspended') {
        const resumeAudio = () => {
          if (this.audioContext?.state === 'suspended') {
            this.audioContext.resume().catch(() => {
              // Silent fail - audio enhancement not critical
            });
          }
        };

        document.addEventListener('click', resumeAudio, { once: true });
        document.addEventListener('touchstart', resumeAudio, { once: true });
      }

      try {
        const oscillator = this.audioContext.createOscillator();
        oscillator.frequency.value = 0;
        const gainNode = this.audioContext.createGain();
        gainNode.gain.value = 0;
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
      } catch (e) {
        console.warn('Audio setup failed:', e);
        this.audioContext = null;
      }
    } catch (error) {
      console.warn('AudioContext not available');
      this.audioContext = null;
    }
  }

  private setupEventHandlers(): void {
    this.visibilityHandler = () => {
      if (document.hidden && this.isExporting) {
        this.enhanceBackgroundOperation();
        this.showBackgroundNotification();
      } else if (!document.hidden && this.isExporting) {
        this.normalizeOperation();
      }
    };

    this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (this.isExporting) {
        this.storage.setItem(
          'export-interrupted',
          JSON.stringify({
            timestamp: Date.now(),
            reason: 'page-unload',
            progress: this.currentProgress,
          })
        );

        e.preventDefault();
        const message = 'Export is still in progress. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
      return undefined;
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  private showBackgroundNotification(): void {
    if (!this.features.hasNotifications) return;

    if (Notification.permission === 'granted') {
      try {
        const notification = new Notification('Export in Progress', {
          body: 'Keep this tab open for faster export. You can switch to other tabs.',
          icon: '/favicon.ico',
          silent: true,
          tag: 'export-progress',
        });

        void notification;
      } catch (e) {
        //
      }
    }
  }

  private cleanupStaleExports(): void {
    const exportStart = this.storage.getItem('export-start');
    const exportState = this.storage.getItem('export-state');
    const exportInterrupted = this.storage.getItem('export-interrupted');

    if (exportInterrupted) {
      try {
        const interrupted = JSON.parse(exportInterrupted);
        console.warn('Previous export interrupted:', interrupted);
      } catch (e) {
        //
      }
      this.storage.removeItem('export-interrupted');
    }

    const now = Date.now();

    if (exportStart) {
      const startTime = parseInt(exportStart, 10);
      if (now - startTime > 3600000) {
        // 1 hour
        this.cleanupLocalStorage();
      }
    }

    if (exportState) {
      try {
        const state = JSON.parse(exportState);
        if (now - state.timestamp > 30000) {
          // 30 seconds
          this.cleanupLocalStorage();
        }
      } catch (e) {
        this.cleanupLocalStorage();
      }
    }
  }

  private enhanceBackgroundOperation(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }

    if (this.features.hasRAF) {
      const rafLoop = () => {
        if (!this.shouldContinue || !document.hidden) {
          this.rafId = null;
          return;
        }

        this.notifyProgress(this.currentProgress, this.currentStatus);
        this.updateStoredState();

        this.rafId = requestAnimationFrame(rafLoop);
      };

      this.rafId = requestAnimationFrame(rafLoop);
    }

    this.heartbeatInterval = setInterval(() => {
      if (!this.shouldContinue) {
        if (this.heartbeatInterval) {
          clearInterval(this.heartbeatInterval);
        }
        return;
      }

      this.notifyProgress(this.currentProgress, this.currentStatus);
      this.updateStoredState();
    }, 100);
  }

  private normalizeOperation(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    if (this.audioContext?.state === 'running') {
      this.audioContext.suspend().catch(() => {});
    }

    this.heartbeatInterval = setInterval(() => {
      if (!this.shouldContinue) {
        if (this.heartbeatInterval) {
          clearInterval(this.heartbeatInterval);
        }
        return;
      }

      this.notifyProgress(this.currentProgress, this.currentStatus);
    }, 500);
  }

  private updateStoredState(): void {
    this.storage.setItem(
      'export-state',
      JSON.stringify({
        progress: this.currentProgress,
        status: this.currentStatus,
        timestamp: Date.now(),
      })
    );
  }

  public onProgress(callback: (progress: number, status: string) => void): () => void {
    this.progressCallbacks.add(callback);
    return () => {
      this.progressCallbacks.delete(callback);
    };
  }

  private notifyProgress(progress: number, status: string): void {
    this.currentProgress = progress;
    this.currentStatus = status;

    this.progressCallbacks.forEach(callback => {
      try {
        callback(progress, status);
      } catch (e) {
        console.warn('Progress callback error:', e);
      }
    });
  }

  public updateProgress(progress: number, status: string): void {
    this.notifyProgress(progress, status);
  }

  /**
   * Optimized batch processing with adaptive yielding
   */
  public async processInBatches<T>(
    data: T[],
    batchSize: number,
    processor: (batch: T[], index: number, total: number) => Promise<void> | void,
    progressCallback?: (current: number, total: number) => void
  ): Promise<void> {
    const totalBatches = Math.ceil(data.length / batchSize);
    const isBackground = typeof document !== 'undefined' && document.hidden;

    this.yieldCount = 0;

    for (let i = 0; i < totalBatches; i++) {
      if (!this.shouldContinue) {
        throw new Error('Export cancelled');
      }

      const start = i * batchSize;
      const end = Math.min(start + batchSize, data.length);
      const batch = data.slice(start, end);

      const result = processor(batch, i, totalBatches);
      if (result instanceof Promise) {
        await result;
      }

      const progress = Math.round((end / data.length) * 100);
      if (progressCallback) {
        progressCallback(end, data.length);
      }

      await this.yieldControl(isBackground);
    }
  }

  /**
   * Smart yield control that adapts to browser state
   */
  private async yieldControl(isBackground: boolean): Promise<void> {
    this.yieldCount++;

    if (!this.features.hasPromises) {
      setTimeout(() => {}, 0);
      return;
    }

    if (isBackground) {
      if (this.audioContext?.state === 'running') {
        await Promise.resolve();
      } else {
        await Promise.resolve();
      }
    } else {
      if (this.yieldCount % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 10));
      } else {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  public async startExport(exportLogic: () => Promise<void>, onError?: (error: string) => void): Promise<void> {
    if (this.isExporting) {
      throw new Error('Export already in progress');
    }

    if (this.features.hasNotifications && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (e) {
        //
      }
    }

    this.isExporting = true;
    this.shouldContinue = true;
    this.currentProgress = 0;
    this.currentStatus = 'Initializing...';
    this.yieldCount = 0;

    if (this.audioContext?.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (e) {
        //
      }
    }

    if (document.hidden) {
      this.enhanceBackgroundOperation();
    } else {
      this.normalizeOperation();
    }

    const startTime = Date.now();
    this.storage.setItem('export-start', startTime.toString());
    this.storage.removeItem('export-interrupted');

    try {
      await exportLogic();

      this.updateProgress(100, 'Export completed successfully!');

      if (document.hidden && this.features.hasNotifications && Notification.permission === 'granted') {
        try {
          const notification = new Notification('Export Complete!', {
            body: 'Your file has been downloaded successfully.',
            icon: '/favicon.ico',
            requireInteraction: false,
            tag: 'export-complete',
          });

          void notification;
        } catch (e) {
          //
        }
      }

      this.cleanupLocalStorage();
    } catch (error: any) {
      this.storage.setItem(
        'export-failed',
        JSON.stringify({
          timestamp: Date.now(),
          error: error.message || 'Unknown error',
          progress: this.currentProgress,
        })
      );

      if (onError) {
        onError(error.message || 'Export failed');
      }

      throw error;
    } finally {
      this.cleanup();
    }
  }

  private cleanupLocalStorage(): void {
    this.storage.removeItem('export-start');
    this.storage.removeItem('export-state');
    this.storage.removeItem('export-interrupted');
    this.storage.removeItem('export-failed');
  }

  private cleanup(): void {
    this.shouldContinue = false;
    this.isExporting = false;
    this.currentProgress = 0;
    this.currentStatus = '';
    this.yieldCount = 0;

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.audioContext?.state === 'running') {
      this.audioContext.suspend().catch(() => {});
    }

    this.progressCallbacks.clear();
  }

  public cancelExport(): void {
    this.storage.setItem(
      'export-interrupted',
      JSON.stringify({
        timestamp: Date.now(),
        reason: 'user-cancelled',
        progress: this.currentProgress,
      })
    );

    this.cleanup();
  }

  public isActive(): boolean {
    return this.isExporting;
  }

  public checkInterruptedExport(): boolean {
    const exportStart = this.storage.getItem('export-start');
    const exportState = this.storage.getItem('export-state');
    const exportInterrupted = this.storage.getItem('export-interrupted');

    if (exportInterrupted) {
      this.storage.removeItem('export-interrupted');
      return true;
    }

    if (exportStart && exportState) {
      try {
        const state = JSON.parse(exportState);
        const now = Date.now();

        if (now - state.timestamp > 30000) {
          this.cleanupLocalStorage();
          return true;
        }
      } catch (e) {
        this.cleanupLocalStorage();
        return true;
      }
    }

    return false;
  }

  public getStoredProgress(): { progress: number; status: string } | null {
    const exportState = this.storage.getItem('export-state');
    if (exportState) {
      try {
        const state = JSON.parse(exportState);
        return { progress: state.progress, status: state.status };
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  public getLastExportStatus(): { success: boolean; error?: string; interrupted?: boolean } | null {
    const failed = this.storage.getItem('export-failed');
    const interrupted = this.storage.getItem('export-interrupted');

    if (failed) {
      try {
        const failedData = JSON.parse(failed);
        this.storage.removeItem('export-failed');
        return { success: false, error: failedData.error };
      } catch (e) {
        return null;
      }
    }

    if (interrupted) {
      try {
        const interruptedData = JSON.parse(interrupted);
        this.storage.removeItem('export-interrupted');
        return { success: false, interrupted: true };
      } catch (e) {
        return null;
      }
    }

    return null;
  }

  public destroy(): void {
    this.cleanup();
    this.cleanupLocalStorage();

    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    if (typeof document !== 'undefined') {
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      }
    }

    BackgroundExportManager.instance = null as any;
  }
}

/**
 * Utility function to detect mobile devices
 */
export function isMobileDevice(): boolean {
  // More comprehensive mobile detection
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(userAgent);

  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const isSmallScreen = window.innerWidth <= 768;

  return isMobileUA || (hasTouch && isSmallScreen);
}

/**
 * Get optimal batch size based on device capabilities
 */
export function getOptimalBatchSize(totalRecords: number): number {
  const isMobile = isMobileDevice();

  let memoryPressure = false;
  if ((performance as any).memory) {
    const memory = (performance as any).memory;
    const usedRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
    memoryPressure = usedRatio > 0.7;
  }

  const isBackground = document.hidden;

  if (memoryPressure) {
    // Low memory: use small batches
    return isMobile ? 50 : 100;
  }

  if (isBackground) {
    // Background: larger batches for fewer yields
    return isMobile ? 300 : 1500;
  }

  // Normal operation
  if (isMobile) {
    // Mobile: balance between memory and performance
    if (totalRecords > 10000) return 200;
    if (totalRecords > 5000) return 300;
    return 500;
  }

  // Desktop: can handle larger batches
  if (totalRecords > 50000) return 1000;
  if (totalRecords > 10000) return 1500;
  return 2000;
}

/**
 * Check if export should show warning
 */
export function shouldWarnLargeExport(totalRecords: number): boolean {
  const isMobile = isMobileDevice();

  if (isMobile && totalRecords > 3000) return true;
  if (!isMobile && totalRecords > 20000) return true;

  return false;
}
